import {
  createFileRoute,
  useNavigate,
  redirect,
  Link,
} from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { ApiError } from "@teamblob/api-client";
import { useAuthStore } from "../stores/auth-store.js";
import { useBodyClass } from "../hooks/use-body-class.js";
import {
  GhostButton,
  AuthErrorBanner,
} from "../components/desktop/auth-primitives.js";
import { Button } from "@teamblob/ui/components";
import * as m from "../paraglide/messages.js";

const searchSchema = z.object({
  error: z.string().optional(),
  /**
   * "1" after a successful email verification, "0" on a failed/expired link.
   * z.coerce.string() because TanStack Router parses `?verified=1` as the
   * number 1 — a bare z.string() would reject it (the original verify-link bug).
   */
  verified: z.coerce.string().optional(),
  /** "1" after a successful password reset — shows a "you can now sign in" banner. */
  reset: z.coerce.string().optional(),
  /** After successful login, redirect to this path (e.g. /accept-invite?token=…). */
  next: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: searchSchema,
  /**
   * Mirror of the `_authenticated` gate, inverted: an already-signed-in user
   * has no business on the sign-in form. Without this, pressing Back from
   * /dashboard lands on a login page the user already passed through — a dead
   * end, since nothing on it moves them forward.
   *
   * Reads the store synchronously, exactly as `_authenticated` does. The store
   * is not persisted (initial state is `isAuthenticated: false, isLoading: true`),
   * so a cold page load falls through to the form and only a client-side
   * navigation with a resolved session can trigger the redirect.
   *
   * Known limit: a hard reload of /login while holding a valid session still
   * renders the form, because nothing calls `checkAuth()` on this route.
   * Covering that would mean gating the most-visited public page behind a
   * spinner — not worth it for an edge case.
   */
  beforeLoad: () => {
    const { isAuthenticated, isLoading, tenant } = useAuthStore.getState();
    if (isAuthenticated && !isLoading) {
      // `replace` is load-bearing, not a preference. This guard usually fires
      // during a Back navigation, so the history pointer is sitting on the
      // /login entry. Replacing it swaps that entry for /dashboard; pushing
      // instead would leave /login behind, and the next Back would land on it
      // and be redirected forward again — an infinite bounce with no way off
      // the site.
      //
      // Workspace-less users still owe us onboarding — same branch as onSubmit.
      throw redirect({
        to: tenant === null ? "/welcome" : "/dashboard",
        replace: true,
      });
    }
  },
  component: LoginPage,
});

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:3002";

function LoginPage() {
  useBodyClass("auth-scroll");
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const {
    error: searchError,
    next: nextPath,
    verified,
    reset,
  } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const oauthError =
    searchError === "oauth" || searchError === "missing_params"
      ? m.login_error_oauth()
      : null;

  // Surface the email-verification outcome (verify → sign in → welcome).
  const verifiedBanner =
    reset === "1"
      ? "Password reset — sign in with your new password."
      : verified === "1"
        ? "Email verified — sign in to continue."
        : verified === "0"
          ? "That verification link is invalid or expired. Sign in to resend it."
          : null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      // Workspace-less users (just verified, no workspace yet) go to onboarding.
      const hasWorkspace = useAuthStore.getState().tenant !== null;
      if (!hasWorkspace) {
        void navigate({ to: "/welcome" });
      } else if (nextPath !== undefined && nextPath !== "") {
        // Post-login redirect (e.g. from the invite acceptance page).
        window.location.href = nextPath;
      } else {
        void navigate({ to: "/dashboard" });
      }
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 401) {
        setFormError(m.login_error_invalid_credentials());
      } else if (err instanceof ApiError && err.statusCode === 403) {
        setFormError(
          "Please verify your email address first. Check your inbox for the verification link.",
        );
      } else {
        setFormError(m.login_error_unexpected());
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-window-face)] p-8 shadow-[var(--shadow-window)]">
        <h1 className="mb-1 text-[22px] font-semibold text-[var(--color-text-strong)]">
          {m.login_title()}
        </h1>
        <p className="mb-6 text-[13px] text-[var(--color-text-muted)]">
          {m.app_tagline()}
        </p>

        {verifiedBanner !== null && verified === "1" ? (
          <div className="mb-4 rounded-[var(--radius-md)] bg-[var(--color-success-soft,#e7f6ec)] px-3 py-2 text-[12px] text-[var(--color-text-success,#1a7f37)]">
            {verifiedBanner}
          </div>
        ) : null}

        {oauthError !== null ? (
          <div className="mb-4">
            <AuthErrorBanner message={oauthError} />
          </div>
        ) : verifiedBanner !== null && verified === "0" ? (
          <div className="mb-4">
            <AuthErrorBanner message={verifiedBanner} />
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div>
            <label className="mb-1 block text-[12px] font-medium text-[var(--color-text-body)]">
              {m.login_email_label()}
            </label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={m.login_email_placeholder()}
              className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] px-3 py-2 text-[14px] text-[var(--color-text-strong)] outline-none focus:border-[var(--color-accent-amber)]"
            />
          </div>

          <div>
            <label className="mb-1 block text-[12px] font-medium text-[var(--color-text-body)]">
              {m.login_password_label()}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={m.login_password_placeholder()}
                className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-window-face)] px-3 py-2 pr-10 text-[14px] text-[var(--color-text-strong)] outline-none focus:border-[var(--color-accent-amber)]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-body)]"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {formError !== null ? (
            <p
              role="alert"
              className="text-[13px] text-[var(--color-text-danger)]"
            >
              {formError}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={submitting}
            disabled={submitting}
          >
            {submitting ? m.login_submitting() : m.login_submit()}
          </Button>

          <div className="text-center text-[12px]">
            <Link
              to="/forgot-password"
              className="text-[var(--color-text-link)] hover:underline"
            >
              {m.login_forgot_password()}
            </Link>
          </div>
        </form>

        {/* OAuth divider */}
        <div className="my-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-[var(--color-border-default)]" />
          <span className="text-[12px] text-[var(--color-text-muted)]">
            {m.login_or_divider()}
          </span>
          <div className="h-px flex-1 bg-[var(--color-border-default)]" />
        </div>

        {/* OAuth buttons */}
        <div className="space-y-2">
          <GhostButton
            fullWidth
            onClick={() => {
              window.location.href = `${API_BASE}/api/v1/auth/google`;
            }}
          >
            {m.login_continue_google()}
          </GhostButton>

          <GhostButton
            fullWidth
            onClick={() => {
              window.location.href = `${API_BASE}/api/v1/auth/microsoft`;
            }}
          >
            {m.login_continue_microsoft()}
          </GhostButton>
        </div>

        <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 text-center text-[12px]">
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            className="font-semibold text-[var(--color-text-link)] hover:underline"
          >
            Create an account
          </Link>
        </div>
      </div>
    </main>
  );
}
